import { GridSortModel } from '@mui/x-data-grid-pro'
import { useState, useCallback } from 'react'

import { FeedbackGridApi } from '../api/feedbackGridApi'
import { FeedbackRow, SortConfig, GridData } from '../types/feedbackGrid.types'

interface UseFeedbackDataProps {
	api: FeedbackGridApi
	initialData: GridData
	itemsPerPage: number
	defaultSort: SortConfig | null
}

export const useFeedbackData = ({ api, initialData, itemsPerPage, defaultSort }: UseFeedbackDataProps) => {
	const [rows, setRows] = useState<FeedbackRow[]>(initialData.data)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [rowCount, setRowCount] = useState(initialData.total)
	const [page, setPage] = useState(0)

	const [sortModel, setSortModel] = useState<GridSortModel>(() => {
		if (defaultSort) {
			const field = defaultSort.column.replace('feedbacks.', '')
			return [
				{
					field,
					sort: defaultSort.order.toLowerCase() as 'asc' | 'desc',
				},
			]
		}
		return []
	})

	const fetchData = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			const sort: SortConfig | null =
				sortModel.length > 0
					? {
							column: sortModel[0].field,
							order: sortModel[0].sort === 'asc' ? 'ASC' : 'DESC',
						}
					: null

			const offset = page * itemsPerPage
			// Filters are applied separately via api.applyFilters
			const result = await api.fetchData(offset, itemsPerPage, {}, sort)

			setRows(result.data || [])
			setRowCount(result.total || 0)
		} catch (err) {
			console.error('FeedbackGrid: Fetch error:', err)
			setError(err instanceof Error ? err.message : 'Failed to fetch data')
		} finally {
			setLoading(false)
		}
	}, [api, page, itemsPerPage, sortModel])

	return {
		rows,
		setRows,
		loading,
		setLoading,
		error,
		rowCount,
		page,
		setPage,
		sortModel,
		setSortModel,
		fetchData,
	}
}
